import { Injectable } from '@angular/core';
import { Observable, of, from } from 'rxjs';
import { map, catchError, concatMap, tap } from 'rxjs/operators';
import { PatientLinkApiService, ThumbnailResponse } from './patient-link-api.service';

interface CacheEntry {
	url: string;
	expiresAt: number;
}

@Injectable({ providedIn: 'root' })
export class PatientThumbnailService {
	// image-api ttl 5 mins.
	private static readonly cacheTtlMs = 4.5 * 60 * 1000;
	private static readonly progressiveBatchSize = 10;
	private cache = new Map<string, CacheEntry>();

	constructor(private readonly api: PatientLinkApiService) {}

	/**
	 * Loads thumbnails progressively in batches of 5.
	 * Each batch emits a Map with all URLs fetched so far (cumulative).
	 * First batch (visible rows) appears fast, rest load in background.
	 */
	getThumbnails(patientIds: string[]): Observable<Map<string, string>> {
		const now = Date.now();
		const result = new Map<string, string>();
		const uncached: string[] = [];

		for (const id of patientIds) {
			const entry = this.cache.get(id);

			if (entry && entry.expiresAt > now) {
				result.set(id, entry.url);
			} else {
				if (entry) {
					this.cache.delete(id);
				}

				uncached.push(id);
			}
		}

		if (uncached.length === 0) {
			return of(result);
		}

		const batches = this.chunk(uncached, PatientThumbnailService.progressiveBatchSize);

		return from(batches).pipe(
			concatMap(batch =>
				this.api.getThumbnails(batch).pipe(
					catchError(() => of([] as ThumbnailResponse[]))
				)
			),
			tap(responses => {
				for (const item of responses) {
					if (item.status === 'found' && item.thumbnails.length > 0) {
						const url = this.pickBestThumbnailUrl(item.thumbnails);

						if (url) {
							this.cache.set(item.invisalignPatientId, {
								url,
								expiresAt: Date.now() + PatientThumbnailService.cacheTtlMs
							});
							result.set(item.invisalignPatientId, url);
						}
					}
				}
			}),
			map(() => new Map(result))
		);
	}

	private chunk(arr: string[], size: number): string[][] {
		const chunks: string[][] = [];

		for (let i = 0; i < arr.length; i += size) {
			chunks.push(arr.slice(i, i + size));
		}

		return chunks;
	}

	/**
	 * Pick the thumbnail with the smallest numeric size, excluding "original".
	 * Sizes from assets: "original", "225", "400", "800", "1200"
	 */
	private pickBestThumbnailUrl(thumbnails: { size: string; url: string }[]): string | null {
		let minSize = Infinity;
		let bestUrl: string | null = null;

		for (const t of thumbnails) {
			const num = parseInt(t.size, 10);

			if (!isNaN(num) && num < minSize) {
				minSize = num;
				bestUrl = t.url;
			}
		}

		return bestUrl;
	}
}
